import { useEffect, useMemo, useState } from 'react';
import { LuPlus } from 'react-icons/lu';
import { MdCancel } from 'react-icons/md';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import { Textarea } from '@/components/Textarea';
import Checkbox from '@/components/Checkbox';
import ReferenceItemsPickerDialog from '@/features/agent/candidates/ai_recommendations/components/ReferenceItemsPickerDialog';
import useFetchAiRecommendationReferenceItems from '@/features/agent/candidates/ai_recommendations/api/useFetchAiRecommendationReferenceItems';
import formatInterviewedAt from '@/features/agent/candidates/ai_recommendations/lib/formatInterviewedAt';
import type { ReferenceSelection } from '@/features/agent/candidates/ai_recommendations/types/referenceSelection';
import type { ResumeField } from './useUploadPersonaFile';
import GenerateIcon from './GenerateIcon';

export type MatchingMethod = 'resume' | 'candidate' | 'text';

/** カスタマイズ再検索時に親へ渡す入力（method により使う項目が異なる） */
export type CustomizeResearchPayload = {
  /** 登録済みの求職者から: 参照情報の絞り込み */
  referenceSelection?: ReferenceSelection;
  /** 登録済みの求職者から: 自由記入プロンプト */
  prompt?: string;
  /** 面談の文字起こしから: 参照テキスト */
  text?: string;
  /** 履歴書から: 参照する項目キー */
  resumeFieldKeys?: string[];
};

const EMPTY_SELECTION: ReferenceSelection = { customFieldIds: [], interviewLogIds: [] };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  method: MatchingMethod;
  // 登録済みの求職者から: 参照情報を取得する候補者
  candidateProfileId?: number | null;
  // テキストから: 参照テキストの初期値（入力済みの文字起こし等）
  initialText?: string;
  // 登録済みの求職者から: 前回の参照情報選択（再オープン時に復元する）
  initialSelection?: ReferenceSelection;
  // 登録済みの求職者から: 前回のプロンプト（再オープン時に復元する）
  initialPrompt?: string;
  // 履歴書から: 抽出した参照項目と前回の選択キー
  resumeFields?: ResumeField[];
  initialResumeKeys?: string[];
  onResearch: (payload: CustomizeResearchPayload) => void;
};

function SectionLabel({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col gap-xs">
      <span className="text-heading-xxs text-main">{title}</span>
      <span className="text-body-xs text-main">{description}</span>
    </div>
  );
}

export default function CustomizeModal({
  open,
  onOpenChange,
  method,
  candidateProfileId = null,
  initialText = '',
  initialSelection = EMPTY_SELECTION,
  initialPrompt = '',
  resumeFields = [],
  initialResumeKeys = [],
  onResearch,
}: Props) {
  const [checkedResumeKeys, setCheckedResumeKeys] = useState<Set<string>>(
    () => new Set(initialResumeKeys)
  );
  const [prompt, setPrompt] = useState(initialPrompt);
  const [text, setText] = useState(initialText);
  const [selection, setSelection] = useState<ReferenceSelection>(initialSelection);
  const [isPicking, setIsPicking] = useState(false);

  const { data: referenceItems, isLoading: isReferenceLoading } =
    useFetchAiRecommendationReferenceItems(
      open && method === 'candidate' ? candidateProfileId : null
    );

  // モーダルを開くたびに前回の入力状態を復元する
  useEffect(() => {
    if (open) {
      setCheckedResumeKeys(new Set(initialResumeKeys));
      setPrompt(initialPrompt);
      setText(initialText);
      setSelection(initialSelection);
      setIsPicking(false);
    }
  }, [open, initialText, initialPrompt, initialSelection, initialResumeKeys]);

  const toggleResumeKey = (key: string) => {
    setCheckedResumeKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // 履歴書は参照項目を最低1つ選ばないと再検索できない（全解除→無言で全項目、の乖離を防ぐ）
  const canResearch = method !== 'resume' || checkedResumeKeys.size > 0;

  // method ごとの入力をまとめて親へ渡す
  const handleResearch = () => {
    if (method === 'candidate') {
      onResearch({ referenceSelection: selection, prompt });
    } else if (method === 'text') {
      onResearch({ text });
    } else {
      onResearch({ resumeFieldKeys: [...checkedResumeKeys] });
    }
  };

  // 選択中のID→ラベルを解決するためのマップ（参照情報データから組み立てる）
  const customFieldLabelMap = useMemo(() => {
    const map = new Map<number, string>();
    referenceItems?.candidate_info_categories.forEach((category) => {
      category.custom_fields.forEach((field) => map.set(field.id, field.name));
    });
    return map;
  }, [referenceItems]);

  const interviewLogLabelMap = useMemo(() => {
    const map = new Map<number, string>();
    referenceItems?.interview_logs.forEach((log) => {
      map.set(log.id, `面談データ ${formatInterviewedAt(log.interviewed_at)}`);
    });
    return map;
  }, [referenceItems]);

  const selectedChips = useMemo(() => {
    const chips: { kind: 'customField' | 'interviewLog'; id: number; label: string }[] = [];
    selection.customFieldIds.forEach((id) => {
      chips.push({ kind: 'customField', id, label: customFieldLabelMap.get(id) ?? '' });
    });
    selection.interviewLogIds.forEach((id) => {
      chips.push({ kind: 'interviewLog', id, label: interviewLogLabelMap.get(id) ?? '' });
    });
    return chips.filter((chip) => chip.label);
  }, [selection, customFieldLabelMap, interviewLogLabelMap]);

  const removeChip = (kind: 'customField' | 'interviewLog', id: number) => {
    setSelection((prev) =>
      kind === 'customField'
        ? { ...prev, customFieldIds: prev.customFieldIds.filter((x) => x !== id) }
        : { ...prev, interviewLogIds: prev.interviewLogIds.filter((x) => x !== id) }
    );
  };

  return (
    <>
      {/* 参照情報選択モーダル表示中はこのダイアログを隠してネスト focus 問題を避ける */}
      <Dialog open={open && !isPicking} onOpenChange={onOpenChange}>
        <DialogContent className="flex w-full max-w-166.25 flex-col overflow-hidden rounded-sm p-0">
          <div className="border-b border-solid border-border px-lg py-md">
            <DialogTitle className="text-heading-xs text-main">AIをカスタマイズ</DialogTitle>
          </div>

          <div className="flex flex-col gap-lg px-lg py-sm">
            {method === 'resume' && (
              <div className="flex flex-col gap-xs">
                <SectionLabel
                  title="参照情報"
                  description="履歴書から読み取った項目のうち、AIが参照するものを調整できます。"
                />
                <div className="flex flex-wrap items-center gap-sm rounded-sm border border-solid border-border p-sm">
                  {resumeFields.map((field) => (
                    <Checkbox
                      key={field.key}
                      checked={checkedResumeKeys.has(field.key)}
                      onChange={() => toggleResumeKey(field.key)}
                    >
                      <span className="text-body-sm text-main" title={field.value}>
                        {field.label}
                      </span>
                    </Checkbox>
                  ))}
                </div>
              </div>
            )}

            {method === 'candidate' && (
              <>
                <div className="flex flex-col gap-xs">
                  <SectionLabel
                    title="参照情報"
                    description="AIが参照する候補者情報の項目を調整できます。"
                  />
                  {selectedChips.length > 0 && (
                    <div className="flex flex-wrap content-center items-center gap-xs rounded-sm border border-solid border-border bg-white p-sm">
                      {selectedChips.map((chip) => (
                        <span
                          key={`${chip.kind}_${chip.id}`}
                          className="inline-flex h-6 items-center gap-xxs rounded-full border border-solid border-border bg-white px-sm text-tag-sm text-main"
                        >
                          {chip.label}
                          <button
                            type="button"
                            onClick={() => removeChip(chip.kind, chip.id)}
                            aria-label={`${chip.label}を参照情報から除外する`}
                            className="flex items-center text-main opacity-50 hover:opacity-100"
                          >
                            <MdCancel size={20} aria-hidden="true" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <Button
                    type="button"
                    color="secondary"
                    size="md"
                    className="self-start"
                    onClick={() => setIsPicking(true)}
                  >
                    <LuPlus size={20} aria-hidden="true" />
                    追加
                  </Button>
                </div>

                <div className="flex flex-col gap-xs">
                  <SectionLabel
                    title="プロンプト"
                    description="検索に関する希望があれば、自由に記入してください。AIが求人選定の参考にします。"
                  />
                  <Textarea
                    value={prompt}
                    onChange={(event) => setPrompt(event.target.value)}
                    className="h-25 resize-none"
                  />
                </div>
              </>
            )}

            {method === 'text' && (
              <div className="flex flex-col gap-xs">
                <SectionLabel title="参照情報" description="AIが参照するテキストを調整できます。" />
                <Textarea
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  className="h-83 resize-none"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-xs border-t border-solid border-border px-lg py-md">
            <Button type="button" color="secondary" size="md" onClick={() => onOpenChange(false)}>
              キャンセル
            </Button>
            <Button
              type="button"
              color="primary"
              size="md"
              disabled={!canResearch}
              onClick={handleResearch}
            >
              <GenerateIcon size={20} className="text-white" />
              この条件で再検索
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {method === 'candidate' && (
        <ReferenceItemsPickerDialog
          open={isPicking}
          onOpenChange={setIsPicking}
          data={referenceItems}
          isLoading={isReferenceLoading}
          value={selection}
          onConfirm={setSelection}
        />
      )}
    </>
  );
}
